import { BaseCommand } from './BaseCommand.js';
import * as fs from 'fs-extra';
import * as path from 'path';
import { promises as fsPromises } from 'fs';

export interface UpdateOptions {
	pug?: boolean;
	meta?: boolean;
	pinia?: boolean;
	testing?: boolean;
	eslint?: boolean;
	prettier?: boolean;
	all?: boolean;
}

export class UpdateCommand extends BaseCommand {
	async execute(options: UpdateOptions): Promise<void> {
		this.log('Updating Frame project...');

		try {
			// Check if we're in a Frame project
			const packageJsonPath = path.join(this.cwd, 'package.json');
			if (!await fs.pathExists(packageJsonPath)) {
				throw new Error('No package.json found. Are you in a project directory?');
			}

			const packageJson = JSON.parse(await fsPromises.readFile(packageJsonPath, "utf8"));
			const isFrameProject = packageJson.dependencies?.['@livx.cc/frame'] ||
				packageJson.devDependencies?.['@livx.cc/frame'];

			if (!isFrameProject) {
				this.log('This doesn\'t appear to be a Frame project. Adding Frame to existing project...');
				await this.addFrameToExistingProject(options);
				return;
			}

			// Apply updates based on options
			if (options.all) {
				await this.updateAll();
			} else {
				if (options.pug) await this.addPugSupport();
				if (options.meta) await this.addMetaManagement();
				if (options.pinia) await this.addPiniaStore();
				if (options.testing) await this.addTestingSetup();
				if (options.eslint) await this.addEslintConfig();
				if (options.prettier) await this.addPrettierConfig();
			}

			this.logSuccess('Frame project updated successfully!');
			this.log('Run `bun install` to install new dependencies');

		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : String(error);
			this.logError(`Failed to update project: ${errorMessage}`);
			throw error;
		}
	}

	private async addFrameToExistingProject(options: UpdateOptions): Promise<void> {
		const packageJsonPath = path.join(this.cwd, 'package.json');
		const packageJson = JSON.parse(await fsPromises.readFile(packageJsonPath, "utf8"));

		// Add Frame dependency
		packageJson.dependencies = packageJson.dependencies || {};
		packageJson.dependencies['@livx.cc/frame'] = '^0.1.0';

		// Add Frame scripts
		packageJson.scripts = packageJson.scripts || {};
		packageJson.scripts.dev = 'frame dev';
		packageJson.scripts.build = 'frame build';
		packageJson.scripts.preview = 'frame preview';

		await fs.outputFile(packageJsonPath, JSON.stringify(packageJson, null, 2));

		// Create basic Frame structure if it doesn't exist
		await this.ensureFrameStructure();

		this.log('Added Frame to existing project');
	}

	private async ensureFrameStructure(): Promise<void> {
		const srcPath = path.join(this.cwd, 'src');
		const publicPath = path.join(this.cwd, 'public');

		// Ensure src directory exists
		await fs.ensureDir(srcPath);
		await fs.ensureDir(path.join(srcPath, 'views'));
		await fs.ensureDir(path.join(srcPath, 'composables'));
		await fs.ensureDir(publicPath);

		// Create basic index.html if it doesn't exist
		const indexHtmlPath = path.join(publicPath, 'index.html');
		if (!await fs.pathExists(indexHtmlPath)) {
			const indexHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Frame App</title>
</head>
<body>
  <div id="app"></div>
</body>
</html>`;
			await fs.outputFile(indexHtmlPath, indexHtml);
		}

		// Create basic Home view if it doesn't exist
		const homeViewPath = path.join(srcPath, 'views', 'Home.vue');
		if (!await fs.pathExists(homeViewPath)) {
			const homeVue = `<template lang="pug">
.home
  h1 Welcome to Frame!
  p Your existing project is now powered by Frame.
</template>

<script setup>
// Your component logic here
</script>

<style scoped>
.home {
  text-align: center;
  padding: 2rem;
}
</style>`;
			await fs.outputFile(homeViewPath, homeVue);
		}
	}

	private async updateAll(): Promise<void> {
		this.log('Adding all Frame features...');
		await this.addPugSupport();
		await this.addMetaManagement();
		await this.addPiniaStore();
		await this.addTestingSetup();
		await this.addEslintConfig();
		await this.addPrettierConfig();
	}

	private async addPugSupport(): Promise<void> {
		this.log('Adding Pug templating support...');

		const packageJsonPath = path.join(this.cwd, 'package.json');
		const packageJson = JSON.parse(await fsPromises.readFile(packageJsonPath, "utf8"));

		packageJson.dependencies = packageJson.dependencies || {};
		packageJson.dependencies['pug'] = '^3.0.2';

		await fs.outputFile(packageJsonPath, JSON.stringify(packageJson, null, 2));

		// Create frame.config.mjs if it doesn't exist
		await this.ensureFrameConfig();

		this.log('✓ Pug support added');
	}

	private async addMetaManagement(): Promise<void> {
		this.log('Adding meta management support...');

		const packageJsonPath = path.join(this.cwd, 'package.json');
		const packageJson = JSON.parse(await fsPromises.readFile(packageJsonPath, "utf8"));

		packageJson.dependencies = packageJson.dependencies || {};
		packageJson.dependencies['@vueuse/head'] = '^2.0.0';

		await fs.outputFile(packageJsonPath, JSON.stringify(packageJson, null, 2));

		// Create frame.config.mjs if it doesn't exist
		await this.ensureFrameConfig();

		this.log('✓ Meta management support added');
	}

	private async addPiniaStore(): Promise<void> {
		this.log('Adding Pinia store...');

		const packageJsonPath = path.join(this.cwd, 'package.json');
		const packageJson = JSON.parse(await fsPromises.readFile(packageJsonPath, "utf8"));

		packageJson.dependencies = packageJson.dependencies || {};
		packageJson.dependencies['pinia'] = '^2.1.6';

		await fs.outputFile(packageJsonPath, JSON.stringify(packageJson, null, 2));

		// Create store directory and basic store
		const storePath = path.join(this.cwd, 'src', 'store');
		await fs.ensureDir(storePath);

		const storeFile = path.join(storePath, 'index.ts');
		if (!await fs.pathExists(storeFile)) {
			const storeContent = `import { defineStore } from 'pinia'

export const useMainStore = defineStore('main', {
  state: () => ({
    count: 0
  }),
  getters: {
    doubleCount: (state) => state.count * 2
  },
  actions: {
    increment() {
      this.count++
    }
  }
})
`;
			await fs.outputFile(storeFile, storeContent);
		}

		this.log('✓ Pinia store added');
	}

	private async addTestingSetup(): Promise<void> {
		this.log('Adding testing setup...');

		const packageJsonPath = path.join(this.cwd, 'package.json');
		const packageJson = JSON.parse(await fsPromises.readFile(packageJsonPath, "utf8"));

		packageJson.devDependencies = packageJson.devDependencies || {};
		packageJson.devDependencies['vitest'] = '^0.34.0';
		packageJson.devDependencies['@vue/test-utils'] = '^2.4.0';
		packageJson.devDependencies['jsdom'] = '^22.1.0';

		packageJson.scripts = packageJson.scripts || {};
		packageJson.scripts.test = 'vitest';

		await fs.outputFile(packageJsonPath, JSON.stringify(packageJson, null, 2));

		// Create basic test setup
		const testsPath = path.join(this.cwd, 'tests');
		await fs.ensureDir(testsPath);

		const vitestConfigPath = path.join(this.cwd, 'vitest.config.ts');
		if (!await fs.pathExists(vitestConfigPath)) {
			const vitestConfig = `import { defineConfig } from 'vitest/config'
import vue from '@vitejs/plugin-vue'

export default defineConfig({
  plugins: [vue()],
  test: {
    globals: true,
    environment: 'jsdom',
  },
})
`;
			await fs.outputFile(vitestConfigPath, vitestConfig);
		}

		this.log('✓ Testing setup added');
	}

	private async addEslintConfig(): Promise<void> {
		this.log('Adding ESLint configuration...');

		const packageJsonPath = path.join(this.cwd, 'package.json');
		const packageJson = JSON.parse(await fsPromises.readFile(packageJsonPath, "utf8"));

		packageJson.devDependencies = packageJson.devDependencies || {};
		packageJson.devDependencies['eslint'] = '^8.45.0';
		packageJson.devDependencies['@typescript-eslint/eslint-plugin'] = '^6.0.0';
		packageJson.devDependencies['@typescript-eslint/parser'] = '^6.0.0';
		packageJson.devDependencies['eslint-plugin-vue'] = '^9.15.0';

		await fs.outputFile(packageJsonPath, JSON.stringify(packageJson, null, 2));

		const eslintConfigPath = path.join(this.cwd, '.eslintrc.js');
		if (!await fs.pathExists(eslintConfigPath)) {
			const eslintConfig = `module.exports = {
  root: true,
  env: {
    node: true,
    es2021: true,
  },
  extends: [
    'eslint:recommended',
    '@vue/eslint-config-typescript',
    'plugin:vue/vue3-essential',
  ],
  parserOptions: {
    ecmaVersion: 'latest',
    sourceType: 'module',
  },
  rules: {
    // Add your custom rules here
  },
}
`;
			await fs.outputFile(eslintConfigPath, eslintConfig);
		}

		this.log('✓ ESLint configuration added');
	}

	private async addPrettierConfig(): Promise<void> {
		this.log('Adding Prettier configuration...');

		const packageJsonPath = path.join(this.cwd, 'package.json');
		const packageJson = JSON.parse(await fsPromises.readFile(packageJsonPath, "utf8"));

		packageJson.devDependencies = packageJson.devDependencies || {};
		packageJson.devDependencies['prettier'] = '^3.0.0';

		await fs.outputFile(packageJsonPath, JSON.stringify(packageJson, null, 2));

		const prettierConfigPath = path.join(this.cwd, '.prettierrc');
		if (!await fs.pathExists(prettierConfigPath)) {
			const prettierConfig = {
				semi: true,
				trailingComma: 'es5',
				singleQuote: true,
				printWidth: 100,
				tabWidth: 2,
				useTabs: false,
			};
			await fs.outputFile(prettierConfigPath, JSON.stringify(prettierConfig, null, 2));
		}

		this.log('✓ Prettier configuration added');
	}

	private async ensureFrameConfig(): Promise<void> {
		const configPath = path.join(this.cwd, 'frame.config.mjs');
		if (!await fs.pathExists(configPath)) {
			const config = `export default {
  // Pug configuration
  pug: {
    pretty: true,
    globals: {},
    filters: {},
  },

  // Meta configuration
  meta: {
    title: 'Frame App',
    description: 'A Vue.js application built with Frame',
    openGraph: {
      siteName: 'Frame App',
      type: 'website',
    },
  },

  // Build configuration
  build: {
    target: 'static',
    outDir: 'dist',
  },
};
`;
			await fs.outputFile(configPath, config);
		}
	}
} 